import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import GoToDashboardButton from './GoToDashboardButton';
import styles from './ProductionRootCause.module.css';
import SourceFileSummary from './SourceFileSummary';
import {
  artavaRechartsAxis,
  artavaRechartsGrid,
  artavaRechartsTooltip,
} from './chartPalette';
import { fetchMarginRiskAnalysis } from './productionRootCause/productionRootCauseApi';

const defaultFilters = {
  startDate: '',
  endDate: '',
  ateSwVersion: '',
  stationName: '',
  selectedFamilies: [],
};

const LimitMarginRisk = () => {
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState({ summary: null, rows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const topRows = useMemo(() => data.rows.slice(0, 12), [data.rows]);

  const loadData = async (nextFilters = filters) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetchMarginRiskAnalysis(nextFilters);
      setData(response || { summary: null, rows: [] });
    } catch (requestError) {
      setError(requestError.message || 'Failed to load limit margin analysis.');
      setData({ summary: null, rows: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(defaultFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    loadData(defaultFilters);
  };

  const summary = data.summary || {};

  return (
    <div className={styles.pageContainer}>
      <GoToDashboardButton />

      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Root problem idea 2</p>
          <h1 className={styles.title}>Limit Margin Risk</h1>
          <p className={styles.description}>
            Look for in-spec numeric measurements that still pass but are drifting too close to their limits. Failure and outside-limit counts stay visible here as supporting evidence, not as the main rank driver.
          </p>
        </div>
      </header>

      <section className={styles.filterPanel}>
        <div className={styles.filterHeader}>
          <div>
            <h3>Filters</h3>
            <p>Use this page to catch weak pressure, voltage, current, or timing steps that are drifting toward the boundary.</p>
          </div>
        </div>
        <div className={styles.filterGrid}>
          <div className={styles.filterItem}>
            <label>Start date</label>
            <input type="date" value={filters.startDate} onChange={(event) => updateFilter('startDate', event.target.value)} />
          </div>
          <div className={styles.filterItem}>
            <label>End date</label>
            <input type="date" value={filters.endDate} onChange={(event) => updateFilter('endDate', event.target.value)} />
          </div>
          <div className={styles.filterItem}>
            <label>ATE SW version</label>
            <input value={filters.ateSwVersion} onChange={(event) => updateFilter('ateSwVersion', event.target.value)} placeholder="All versions" />
          </div>
          <div className={styles.filterItem}>
            <label>Station name</label>
            <input value={filters.stationName} onChange={(event) => updateFilter('stationName', event.target.value)} placeholder="All stations" />
          </div>
          <div className={styles.filterActions}>
            <button className={styles.primaryButton} onClick={() => loadData()} disabled={loading}>Run analysis</button>
            <button className={styles.secondaryButton} onClick={resetFilters}>Reset</button>
          </div>
        </div>
        <SourceFileSummary
          compact
          startDate={filters.startDate}
          endDate={filters.endDate}
          ateSwVersion={filters.ateSwVersion}
          selectedFamilies={filters.selectedFamilies}
          onFamiliesChange={(values) => updateFilter('selectedFamilies', values)}
          title="Source files used for dashboard charts"
        />
      </section>

      {error && <div className={styles.error}>{error}</div>}

      <section className={styles.metricGrid}>
        <div className={styles.metricCard}><span>Numeric rows</span><strong>{summary.numericRows || 0}</strong></div>
        <div className={styles.metricCard}><span>Comparable rows</span><strong>{summary.comparableRows || 0}</strong></div>
        <div className={styles.metricCard}><span>Passing rows analyzed</span><strong>{summary.passRowsAnalyzed || 0}</strong></div>
        <div className={styles.metricCard}><span>Risky steps</span><strong>{summary.riskySteps || 0}</strong></div>
        <div className={styles.metricCard}><span>Top risk score</span><strong>{summary.topRiskScore || 0}</strong></div>
      </section>

      {loading ? (
        <div className={styles.loading}>Calculating process margins...</div>
      ) : data.rows.length === 0 ? (
        <div className={styles.emptyState}>No numeric limit risks matched this slice.</div>
      ) : (
        <>
          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Risk score and near-limit passing rate</h3>
                <p>The ranking now prioritizes passing rows that sit close to their specification boundary. Outside-limit and fail counts are shown separately as evidence.</p>
              </div>
            </div>
            <div className={styles.chartBox}>
              <ResponsiveContainer>
                <ComposedChart data={topRows} margin={{ top: 10, right: 20, bottom: 45, left: 0 }}>
                  <CartesianGrid {...artavaRechartsGrid} vertical={false} />
                  <XAxis dataKey="chartLabel" angle={-35} textAnchor="end" height={75} {...artavaRechartsAxis} />
                  <YAxis yAxisId="left" {...artavaRechartsAxis} />
                  <YAxis yAxisId="right" orientation="right" {...artavaRechartsAxis} />
                  <Tooltip {...artavaRechartsTooltip} />
                  <Bar yAxisId="left" dataKey="riskScore" name="Risk score" fill="#4f7f99" radius={[6, 6, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="passNearLimitRate" name="Near-limit passing %" stroke="#24745f" strokeWidth={3} dot={{ r: 4, fill: '#24745f' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Worst passing margin percent</h3>
                <p>This only uses passing in-spec rows. Very small positive margins signal weak process room even before formal failures appear.</p>
              </div>
            </div>
            <div className={styles.chartBox}>
              <ResponsiveContainer>
                <BarChart data={topRows} margin={{ top: 10, right: 20, bottom: 45, left: 0 }}>
                  <CartesianGrid {...artavaRechartsGrid} vertical={false} />
                  <XAxis dataKey="chartLabel" angle={-35} textAnchor="end" height={75} {...artavaRechartsAxis} />
                  <YAxis {...artavaRechartsAxis} />
                  <Tooltip {...artavaRechartsTooltip} />
                  <Bar dataKey="worstPassMarginPercent" name="Worst passing margin %" fill="#8a3f5d" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className={styles.insightGrid}>
            {data.rows.slice(0, 3).map((row) => (
              <article className={styles.insightCard} key={row.rowKey}>
                <p className={styles.eyebrow}>Weak process signal</p>
                <h3>{row.stepNumber} - {row.stepName}</h3>
                <p>{row.passNearLimitRate}% of passing rows are near-limit, {row.passTightRate}% are very tight, and the worst passing margin is {row.worstPassMarginPercent ?? '-'}%.</p>
                <div className={styles.chips}>
                  <span>{row.limitDescription}</span>
                  <span>{row.measureType}</span>
                  <span>{row.units || 'unitless'}</span>
                  {row.outsideCount > 0 && <span>{row.outsideCount} outside-limit</span>}
                  {row.cpk !== null && <span>Cpk {row.cpk}</span>}
                </div>
              </article>
            ))}
          </section>

          <section className={styles.tablePanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Margin evidence</h3>
                <p>Investigate the highest risk rows first, then check whether their outside-limit and fail evidence confirms an active problem or only an early drift signal.</p>
              </div>
            </div>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Step</th>
                  <th>Limits</th>
                  <th>Risk</th>
                  <th>Near pass</th>
                  <th>Tight pass</th>
                  <th>Outside</th>
                  <th>Worst pass margin</th>
                  <th>Avg pass margin</th>
                  <th>Average</th>
                  <th>Std dev</th>
                  <th>Cpk</th>
                  <th>Rows</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, index) => (
                  <tr key={row.rowKey}>
                    <td>{index + 1}</td>
                    <td className={styles.stepName}>
                      {row.stepNumber}
                      <br />
                      <span className={styles.muted}>{row.stepName}</span>
                      <br />
                      <span className={styles.muted}>{row.testspec || 'All specs'} | {row.ateSwVersions?.join(', ') || 'mixed version'}</span>
                    </td>
                    <td>{row.limitDescription}</td>
                    <td>{row.riskScore}</td>
                    <td>{row.passNearLimitCount} ({row.passNearLimitRate}%)</td>
                    <td>{row.passTightCount} ({row.passTightRate}%)</td>
                    <td>{row.outsideCount} ({row.outsideRate}%)</td>
                    <td>{row.worstPassMarginPercent === null ? '-' : `${row.worstPassMarginPercent}%`}</td>
                    <td>{row.averagePassMarginPercent === null ? '-' : `${row.averagePassMarginPercent}%`}</td>
                    <td>{row.averageValue} {row.units}</td>
                    <td>{row.stdDev}</td>
                    <td>{row.cpk === null ? '-' : row.cpk}</td>
                    <td>{row.passComparableCount} / {row.comparableCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
};

export default LimitMarginRisk;
